/**
 * Coalesce scrub input without repeatedly interrupting the browser decoder.
 * Only the newest target is kept while a seek is in flight; the next target
 * starts on a paint tick after seeked. No frame queue grows with pointer speed.
 */
export function createVideoSeekScheduler({
  requestFrame = callback => requestAnimationFrame(callback),
  cancelFrame = id => cancelAnimationFrame(id),
  tolerance = 1 / 240,
} = {}) {
  let video = null;
  let target = null;
  let frame = null;
  let inFlight = false;
  let disposed = false;
  let failed = false;
  let watchdog = null;
  const waiters = new Set();

  const idle = () => target === null && frame === null && !inFlight && !video?.seeking;
  const resolveWaiters = result => {
    for (const waiter of waiters) {
      clearTimeout(waiter.timer);
      waiter.resolve(result);
    }
    waiters.clear();
  };
  const clearWatchdog = () => {
    clearTimeout(watchdog);
    watchdog = null;
  };
  const notifyIdle = () => {
    if (idle()) resolveWaiters(!failed);
  };
  const monitor = () => {
    if (watchdog !== null || disposed) return;
    // Recover if a browser omits seeked, including a slow seek that outlives
    // awaitSettled's deadline. One cancellable timer never interrupts a seek.
    watchdog = setTimeout(() => {
      watchdog = null;
      if (disposed) return;
      if (video?.seeking) monitor();
      else {
        inFlight = false;
        schedule();
      }
    }, 100);
  };
  const flush = () => {
    frame = null;
    if (disposed || !video) return;
    if (inFlight || video.seeking) { monitor(); return; }
    if (target === null) { notifyIdle(); return; }
    const next = target;
    target = null;
    if (Math.abs(video.currentTime - next) <= tolerance) {
      notifyIdle();
      return;
    }
    inFlight = true;
    try {
      video.currentTime = next;
      monitor();
    } catch (_) {
      inFlight = false;
      failed = true;
      resolveWaiters(false);
    }
  };
  const schedule = () => {
    if (disposed) return;
    if (inFlight || video?.seeking) { monitor(); return; }
    if (target === null) { notifyIdle(); return; }
    if (frame === null) frame = requestFrame(flush);
  };
  const onSeeked = () => {
    inFlight = false;
    clearWatchdog();
    schedule();
  };
  const onUnavailable = () => {
    if (frame !== null) cancelFrame(frame);
    frame = null;
    target = null;
    inFlight = false;
    failed = true;
    clearWatchdog();
    resolveWaiters(false);
  };
  const detach = () => {
    video?.removeEventListener('seeked', onSeeked);
    video?.removeEventListener('error', onUnavailable);
    video?.removeEventListener('emptied', onUnavailable);
  };

  return {
    request(nextVideo, seconds) {
      if (disposed || !nextVideo || !Number.isFinite(seconds)) return;
      if (video !== nextVideo) {
        detach();
        onUnavailable();
        video = nextVideo;
        video.addEventListener('seeked', onSeeked);
        video.addEventListener('error', onUnavailable);
        video.addEventListener('emptied', onUnavailable);
      }
      failed = false;
      target = Math.max(0, seconds);
      schedule();
    },
    awaitSettled(timeoutMs = 400) {
      if (disposed || failed) return Promise.resolve(false);
      if (idle()) return Promise.resolve(true);
      return new Promise(resolve => {
        const waiter = { resolve, timer: null };
        waiter.timer = setTimeout(() => {
          waiters.delete(waiter);
          resolve(false);
        }, Math.max(0, Math.min(400, Number(timeoutMs) || 0)));
        waiters.add(waiter);
      });
    },
    dispose() {
      disposed = true;
      detach();
      onUnavailable();
      video = null;
    },
  };
}
